import { Avatar, Button, Group, NativeSelect, Stack, Table, Text, TextInput, Tooltip } from "@mantine/core";
import { IconPlus, IconSearch, IconUpload } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { ResumeLink } from "../components/ResumeLink";
import { ShareIntakeLinkButton } from "../components/ShareIntakeLinkButton";
import { CandidatePoolPromise } from "../components/CandidatePoolPromise";
import { ContactActions } from "../components/matching/ContactActions";
import { ContactStatus } from "../components/matching/ContactStatus";
import { FitTierBadge } from "../components/matching/FitTierBadge";
import { LogContactModal } from "../components/matching/LogContactModal";
import { JobPickerModal } from "../components/explore/JobPickerModal";
import { SubmitCandidateModal, SUBMIT_TO_JOB_LABEL } from "../components/SubmitCandidateModal";
import { CLEAR_FILTERS_LABEL, DataTableShell, FilterBar, PageHeader, SortableTh, StatusBadge, useInfinitePage, useTableSort } from "../components/ui";
import {
  CANDIDATE_LIST_SORT_BEST_MATCH,
  CANDIDATE_LIST_SORT_OPTIONS,
  CANDIDATE_MATCHES_COLUMN,
  CANDIDATE_PIPELINE_COLUMN,
  CANDIDATE_PROFILE_COLUMN,
  CANDIDATE_RECRUITER_COLUMN,
  CANDIDATE_VERIFIED_TOOLTIP,
  CANDIDATES_EMPTY_ADD_DESCRIPTION,
  CANDIDATES_EMPTY_POOL_DESCRIPTION,
  CANDIDATES_EMPTY_TITLE,
  CHANNEL_COLUMN_LABEL,
  CONTACT_FILTER_ANY,
  CONTACT_FILTER_OPTIONS,
  LOG_CONTACT_LABEL,
  MATCH_TABLE_CANDIDATE,
  MATCH_TIER_FILTER_ANY,
  MATCH_TIER_FILTER_OPTIONS,
  MORE_MATCHES_MANY,
  MORE_MATCHES_ONE,
  intakeChannelLabel,
} from "../constants/matching";
import { candidatesApi, intakeApi } from "../lib/api";
import { useQueryFilters } from "../lib/useQueryFilters";
import { NAV_COPY, NAV_ICONS } from "../lib/navCopy";
import { formatCities, formatExperience } from "../lib/format";
import { axioTokens } from "../theme";
import type { Candidate, CandidateLastSubmission, Job, Page } from "../types/api";

const CANDIDATE_CELL_LINE_CLAMP = axioTokens.number.one;
const CANDIDATE_CELL_FILL_STYLE = {
  minWidth: "var(--axio-unit-0)",
  maxWidth: "var(--axio-percentage-full)",
  overflow: "hidden",
} as const;
const CANDIDATE_CELL_CLIP_STYLE = {
  overflow: "hidden",
} as const;
const CANDIDATE_TABLE_STYLE = {
  tableLayout: "fixed",
  width: "var(--axio-percentage-full)",
} as const;
const CANDIDATE_PIPELINE_FONT_SIZE = "var(--axio-font-size-micro)";
const CANDIDATE_PIPELINE_DETAIL_GAP = "var(--axio-unit-0)";
const CANDIDATE_PIPELINE_LINE_HEIGHT = "var(--axio-line-height-compact)";
export const ACTION_COLUMN_LABEL = "Action";
const CANDIDATE_ACTION_BUTTON_SIZE = "xs";
const CANDIDATE_ACTION_BUTTON_VARIANT = "default";
const CANDIDATE_SORT_SELECT_DATA = [...CANDIDATE_LIST_SORT_OPTIONS];
const MATCH_TIER_SELECT_DATA = [...MATCH_TIER_FILTER_OPTIONS];
const CONTACT_FILTER_SELECT_DATA = [...CONTACT_FILTER_OPTIONS];
const CANDIDATE_FILTER_DEFAULTS = {
  q: "",
  sort: CANDIDATE_LIST_SORT_BEST_MATCH,
  match: MATCH_TIER_FILTER_ANY,
  contact: CONTACT_FILTER_ANY,
};
const CANDIDATE_FILTER_ALLOWED = {
  sort: CANDIDATE_SORT_SELECT_DATA.map((item) => item.value),
  match: MATCH_TIER_SELECT_DATA.map((item) => item.value),
  contact: CONTACT_FILTER_SELECT_DATA.map((item) => item.value),
};
const CANDIDATE_CLEAR_FILTER_KEYS = ["q", "match", "contact"] as const;
const CANDIDATE_AVATAR_VERIFIED_STYLE = {
  border: `${axioTokens.unit[2]}px solid ${axioTokens.money}`,
} as const;
const PROFILE_DETAIL_SEPARATOR = " · ";

function TruncatedLine({
  value,
  size,
  fz,
  lh,
  dimmed = false,
}: {
  value: string;
  size?: "sm" | "xs";
  fz?: string;
  lh?: string;
  dimmed?: boolean;
}) {
  return (
    <Text
      size={size}
      fz={fz}
      lh={lh}
      c={dimmed ? "dimmed" : undefined}
      lineClamp={CANDIDATE_CELL_LINE_CLAMP}
      title={value}
      style={CANDIDATE_CELL_FILL_STYLE}
    >
      {value}
    </Text>
  );
}

function ProfileCell({
  title,
  years,
  cities,
}: {
  title: string | null;
  years: number | null;
  cities: string[] | null | undefined;
}) {
  const empty = formatExperience(null);
  const role = title?.trim() ?? "";
  const experience = years != null ? formatExperience(years) : "";
  const location = formatCities(cities);
  const details = [experience && experience !== empty ? experience : "", location !== empty ? location : ""]
    .filter(Boolean)
    .join(PROFILE_DETAIL_SEPARATOR);
  if (!role && !details) {
    return <Text size="sm">{empty}</Text>;
  }
  return (
    <Stack gap="var(--axio-unit-2)" style={CANDIDATE_CELL_FILL_STYLE}>
      {role ? <TruncatedLine value={role} size="sm" /> : null}
      {details ? <TruncatedLine value={details} size="xs" dimmed /> : null}
    </Stack>
  );
}

function moreSubmissionsLabel(activeCount: number): string {
  return `+${activeCount - axioTokens.number.one} more`;
}

function moreMatchesLabel(remaining: number): string {
  const template = remaining === axioTokens.number.one ? MORE_MATCHES_ONE : MORE_MATCHES_MANY;
  return template.replace("{n}", String(remaining));
}

function BestMatchCell({ summary }: { summary: Candidate["match_summary"] }) {
  const matchCount = (summary?.strong_count ?? axioTokens.number.zero) + (summary?.good_count ?? axioTokens.number.zero);
  const top = summary?.top;
  if (!summary || matchCount === axioTokens.number.zero || !top) {
    return <Text size="sm" c="dimmed">—</Text>;
  }
  const remaining = matchCount - axioTokens.number.one;
  return (
    <Stack gap="var(--axio-unit-2)" style={CANDIDATE_CELL_FILL_STYLE}>
      <FitTierBadge tier={top.fit_tier} score={top.fit_score} />
      {top.job_title ? (
        <TruncatedLine
          value={top.job_title}
          fz={CANDIDATE_PIPELINE_FONT_SIZE}
          lh={CANDIDATE_PIPELINE_LINE_HEIGHT}
        />
      ) : null}
      {remaining > axioTokens.number.zero ? (
        <TruncatedLine
          value={moreMatchesLabel(remaining)}
          fz={CANDIDATE_PIPELINE_FONT_SIZE}
          lh={CANDIDATE_PIPELINE_LINE_HEIGHT}
          dimmed
        />
      ) : null}
    </Stack>
  );
}

function PipelineCell({
  last,
  activeCount,
  submissionsBasePath,
}: {
  last: CandidateLastSubmission | null;
  activeCount: number;
  submissionsBasePath: string;
}) {
  if (!last) {
    return <StatusBadge status="in_pool" />;
  }
  const extra = activeCount > axioTokens.number.one ? moreSubmissionsLabel(activeCount) : "";
  return (
    <Stack gap="var(--axio-unit-2)" style={CANDIDATE_CELL_FILL_STYLE}>
      <StatusBadge status={last.status} />
      <Stack gap={CANDIDATE_PIPELINE_DETAIL_GAP}>
        {last.job_title ? (
          <Text
            component={Link}
            to={`${submissionsBasePath}/${last.submission_id}`}
            fz={CANDIDATE_PIPELINE_FONT_SIZE}
            lh={CANDIDATE_PIPELINE_LINE_HEIGHT}
            lineClamp={CANDIDATE_CELL_LINE_CLAMP}
            title={last.job_title}
            style={{ textDecoration: "none", color: "inherit", ...CANDIDATE_CELL_FILL_STYLE }}
          >
            {last.job_title}
          </Text>
        ) : null}
        {extra ? (
          <TruncatedLine
            value={extra}
            fz={CANDIDATE_PIPELINE_FONT_SIZE}
            lh={CANDIDATE_PIPELINE_LINE_HEIGHT}
            dimmed
          />
        ) : null}
      </Stack>
    </Stack>
  );
}

function CandidateAvatar({
  name,
  to,
  verified,
}: {
  name: string;
  to: string;
  verified: boolean;
}) {
  const avatar = (
    <Avatar
      color="violet"
      radius={axioTokens.radiusCircle}
      size="var(--axio-unit-32)"
      style={verified ? CANDIDATE_AVATAR_VERIFIED_STYLE : undefined}
    >
      {name[0]?.toUpperCase() ?? "?"}
    </Avatar>
  );
  const linked = (
    <Link to={to} style={{ textDecoration: "none", color: "inherit", display: "inline-flex" }}>
      {avatar}
    </Link>
  );
  if (!verified) return linked;
  return (
    <Tooltip label={CANDIDATE_VERIFIED_TOOLTIP} withArrow>
      <span aria-label={CANDIDATE_VERIFIED_TOOLTIP} style={{ display: "inline-flex" }}>
        {linked}
      </span>
    </Tooltip>
  );
}

export function CandidatesPage({
  basePath = "/candidates",
  submissionsBasePath = "/submissions",
  listCandidates,
  title = NAV_COPY.recruiter.myCandidates,
  subtitle = "Your candidate pool, ready to submit to open jobs.",
  formatSubtitle,
  emptyTitle = CANDIDATES_EMPTY_TITLE,
  emptyDescription,
  showAddCandidate = true,
  showRecruiterColumn = false,
  showChannelColumn = false,
}: {
  basePath?: string;
  submissionsBasePath?: string;
  listCandidates?: (params: {
    q?: string;
    page?: number;
    page_size?: number;
    sort?: string;
    match_tier?: string;
    contact?: string;
  }) => Promise<Page<Candidate>>;
  title?: string;
  subtitle?: string;
  formatSubtitle?: (total: number) => string;
  emptyTitle?: string;
  emptyDescription?: string;
  showAddCandidate?: boolean;
  showRecruiterColumn?: boolean;
  showChannelColumn?: boolean;
}) {
  const candidateFilters = useQueryFilters(CANDIDATE_FILTER_DEFAULTS, {
    debounceKeys: ["q"],
    allowed: CANDIDATE_FILTER_ALLOWED,
  });
  const search = candidateFilters.draft.q;
  const sort = candidateFilters.draft.sort;
  const matchTier = candidateFilters.draft.match;
  const contactFilter = candidateFilters.draft.contact;
  const appliedSearch = candidateFilters.values.q;
  const appliedSort = candidateFilters.values.sort;
  const appliedMatch = candidateFilters.values.match;
  const appliedContact = candidateFilters.values.contact;
  const [submitCandidate, setSubmitCandidate] = useState<{ id: string; name: string } | null>(null);
  const [submitJobId, setSubmitJobId] = useState<string | null>(null);
  const [jobPickerOpen, setJobPickerOpen] = useState(false);
  const [logCandidateId, setLogCandidateId] = useState<string | null>(null);

  const candidatesQuery = useInfinitePage(
    ["candidates", basePath, appliedSearch, appliedSort, appliedMatch, appliedContact],
    (page, pageSize) =>
      (listCandidates ?? candidatesApi.list)({
        q: appliedSearch || undefined,
        page,
        page_size: pageSize,
        sort: appliedSort,
        match_tier: appliedMatch !== MATCH_TIER_FILTER_ANY ? appliedMatch : undefined,
        contact: appliedContact !== CONTACT_FILTER_ANY ? appliedContact : undefined,
      }),
  );

  const intakeLinksQuery = useQuery({
    queryKey: ["my-intake-links"],
    queryFn: intakeApi.listLinks,
    enabled: showAddCandidate,
  });
  const activeIntakeLink = (intakeLinksQuery.data ?? []).find((link) => link.is_active) ?? null;
  const items = candidatesQuery.items;
  const headerSubtitle =
    formatSubtitle && candidatesQuery.isFetched
      ? formatSubtitle(candidatesQuery.total)
      : subtitle;
  const emptyCopy =
    emptyDescription ??
    (showAddCandidate ? CANDIDATES_EMPTY_ADD_DESCRIPTION : CANDIDATES_EMPTY_POOL_DESCRIPTION);
  const hasFilters = Boolean(
    search ||
      (matchTier && matchTier !== MATCH_TIER_FILTER_ANY) ||
      (contactFilter && contactFilter !== CONTACT_FILTER_ANY),
  );

  function clearFilters() {
    candidateFilters.clear(CANDIDATE_CLEAR_FILTER_KEYS);
  }

  const { sorted, sortKey, direction, toggle } = useTableSort(items, {
    name: (r) => r.full_name?.toLowerCase() ?? "",
    profile: (r) => r.current_title?.toLowerCase() ?? "",
    pipeline: (r) => r.last_submission?.status ?? "in_pool",
    resume: (r) => Boolean(r.resume_url),
  });

  function startSubmit(candidate: Candidate) {
    setSubmitCandidate({ id: candidate.id, name: candidate.full_name });
    setSubmitJobId(null);
    setJobPickerOpen(true);
  }

  function pickSubmitJob(job: Job) {
    setSubmitJobId(job.id);
  }

  function closeSubmit() {
    setSubmitJobId(null);
    setSubmitCandidate(null);
  }

  return (
    <Stack gap="lg">
      <PageHeader
        icon={NAV_ICONS.candidates}
        title={title}
        subtitle={headerSubtitle}
        actions={
          showAddCandidate ? (
          <Group gap="sm">
            <ShareIntakeLinkButton existingLink={activeIntakeLink} label="Share QR link" variant="light" />
            <Button
              component={Link}
              to="/candidates/bulk-upload"
              variant="default"
              leftSection={<IconUpload size="var(--axio-unit-16)" />}
            >
              Bulk upload
            </Button>
            <Button
              component={Link}
              to="/candidates/new"
              target="_blank"
              rel="noopener noreferrer"
              leftSection={<IconPlus size="var(--axio-unit-16)" />}
              variant="filled"
              color="violet"
            >
              Add candidate
            </Button>
          </Group>
          ) : undefined
        }
      />

      {showAddCandidate && <CandidatePoolPromise />}

      <FilterBar
        actions={
          <Button variant="default" onClick={clearFilters} disabled={!hasFilters}>
            {CLEAR_FILTERS_LABEL}
          </Button>
        }
      >
        <TextInput
          placeholder="Search by name"
          leftSection={<IconSearch size="var(--axio-unit-16)" />}
          value={search}
          onChange={(e) => candidateFilters.set({ q: e.currentTarget.value })}
          w="var(--axio-unit-280)"
        />
        <NativeSelect
          data={CANDIDATE_SORT_SELECT_DATA}
          value={sort}
          onChange={(event) => candidateFilters.set({ sort: event.currentTarget.value })}
          w="var(--axio-unit-200)"
        />
        <NativeSelect
          data={MATCH_TIER_SELECT_DATA}
          value={matchTier}
          onChange={(event) => candidateFilters.set({ match: event.currentTarget.value })}
          w="var(--axio-unit-200)"
        />
        <NativeSelect
          data={CONTACT_FILTER_SELECT_DATA}
          value={contactFilter}
          onChange={(event) => candidateFilters.set({ contact: event.currentTarget.value })}
          w="var(--axio-unit-200)"
        />
      </FilterBar>

      <DataTableShell
        loading={candidatesQuery.isLoading}
        error={
          candidatesQuery.isError
            ? candidatesQuery.error instanceof Error
              ? candidatesQuery.error.message
              : undefined
            : undefined
        }
        onRetry={() => void candidatesQuery.refetch()}
        hasMore={candidatesQuery.hasMore}
        loadingMore={candidatesQuery.loadingMore}
        onLoadMore={candidatesQuery.loadMore}
        isEmpty={items.length === 0}
        empty={{
          icon: NAV_ICONS.candidates,
          title: emptyTitle,
          description: emptyCopy,
          action: showAddCandidate ? (
            <Button
              component={Link}
              to="/candidates/new"
              target="_blank"
              rel="noopener noreferrer"
              variant="default"
            >
              Add your first candidate
            </Button>
          ) : undefined,
        }}
      >
        <Table style={CANDIDATE_TABLE_STYLE}>
          <Table.Thead>
            <Table.Tr>
              <SortableTh columnKey="name" sortKey={sortKey} direction={direction} onSort={toggle}>
                {MATCH_TABLE_CANDIDATE}
              </SortableTh>
              {showRecruiterColumn && <Table.Th>{CANDIDATE_RECRUITER_COLUMN}</Table.Th>}
              <SortableTh columnKey="profile" sortKey={sortKey} direction={direction} onSort={toggle}>
                {CANDIDATE_PROFILE_COLUMN}
              </SortableTh>
              {showChannelColumn ? <Table.Th>{CHANNEL_COLUMN_LABEL}</Table.Th> : null}
              <SortableTh columnKey="pipeline" sortKey={sortKey} direction={direction} onSort={toggle}>
                {CANDIDATE_PIPELINE_COLUMN}
              </SortableTh>
              <Table.Th>{CANDIDATE_MATCHES_COLUMN}</Table.Th>
              <SortableTh columnKey="resume" sortKey={sortKey} direction={direction} onSort={toggle}>
                {ACTION_COLUMN_LABEL}
              </SortableTh>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {sorted.map((c) => {
              const last = c.last_submission ?? null;
              const activeCount = c.active_submission_count ?? 0;
              return (
                <Table.Tr key={c.id}>
                  <Table.Td style={CANDIDATE_CELL_CLIP_STYLE}>
                    <Group gap="sm" wrap="nowrap" align="flex-start">
                      <CandidateAvatar
                        name={c.full_name}
                        to={`${basePath}/${c.id}`}
                        verified={Boolean(c.phone_verified_at)}
                      />
                      <Stack gap="var(--axio-unit-0)" style={{ ...CANDIDATE_CELL_FILL_STYLE, flex: "var(--axio-number-one)" }}>
                        <Text
                          component={Link}
                          to={`${basePath}/${c.id}`}
                          fw="var(--axio-font-weight-semibold)"
                          size="sm"
                          lineClamp={CANDIDATE_CELL_LINE_CLAMP}
                          title={c.full_name}
                          style={{ color: axioTokens.ink, textDecoration: "none", ...CANDIDATE_CELL_FILL_STYLE }}
                        >
                          {c.full_name}
                        </Text>
                        <ContactActions
                          phone={c.phone}
                          phoneLabel={c.phone || "No phone"}
                          candidateId={c.id}
                        />
                        {c.last_contacted_at ? (
                          <ContactStatus
                            lastContactedAt={c.last_contacted_at}
                            lastContactOutcome={c.last_contact_outcome}
                            size="xs"
                            lineClamp={CANDIDATE_CELL_LINE_CLAMP}
                          />
                        ) : null}
                      </Stack>
                    </Group>
                  </Table.Td>
                  {showRecruiterColumn && (
                    <Table.Td style={CANDIDATE_CELL_CLIP_STYLE}>
                      <TruncatedLine value={c.recruiter_name ?? "—"} size="sm" />
                    </Table.Td>
                  )}
                  <Table.Td style={CANDIDATE_CELL_CLIP_STYLE}>
                    <ProfileCell title={c.current_title} years={c.years_experience} cities={c.cities} />
                  </Table.Td>
                  {showChannelColumn ? (
                    <Table.Td style={CANDIDATE_CELL_CLIP_STYLE}>
                      <TruncatedLine value={intakeChannelLabel(c.source_channel)} size="sm" />
                    </Table.Td>
                  ) : null}
                  <Table.Td style={CANDIDATE_CELL_CLIP_STYLE}>
                    <PipelineCell
                      last={last}
                      activeCount={activeCount}
                      submissionsBasePath={submissionsBasePath}
                    />
                  </Table.Td>
                  <Table.Td style={CANDIDATE_CELL_CLIP_STYLE}>
                    <BestMatchCell summary={c.match_summary} />
                  </Table.Td>
                  <Table.Td style={CANDIDATE_CELL_CLIP_STYLE}>
                    <Group gap="xs" wrap="wrap">
                      {showAddCandidate ? (
                        <Button
                          size={CANDIDATE_ACTION_BUTTON_SIZE}
                          variant={CANDIDATE_ACTION_BUTTON_VARIANT}
                          onClick={() => setLogCandidateId(c.id)}
                        >
                          {LOG_CONTACT_LABEL}
                        </Button>
                      ) : null}
                      <ResumeLink
                        url={c.resume_url}
                        variant="button"
                        size={CANDIDATE_ACTION_BUTTON_SIZE}
                        buttonVariant={CANDIDATE_ACTION_BUTTON_VARIANT}
                        showWhenEmpty
                      />
                      {showAddCandidate ? (
                        <Button
                          size={CANDIDATE_ACTION_BUTTON_SIZE}
                          variant={CANDIDATE_ACTION_BUTTON_VARIANT}
                          onClick={() => startSubmit(c)}
                        >
                          {SUBMIT_TO_JOB_LABEL}
                        </Button>
                      ) : null}
                    </Group>
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </DataTableShell>
      {showAddCandidate ? (
        <>
          <JobPickerModal opened={jobPickerOpen} onClose={() => setJobPickerOpen(false)} onPick={pickSubmitJob} />
          <SubmitCandidateModal
            jobId={submitJobId ?? ""}
            opened={!!submitJobId && !!submitCandidate}
            candidateId={submitCandidate?.id}
            candidateName={submitCandidate?.name}
            onClose={closeSubmit}
          />
          {logCandidateId ? (
            <LogContactModal
              opened
              onClose={() => setLogCandidateId(null)}
              candidateId={logCandidateId}
            />
          ) : null}
        </>
      ) : null}
    </Stack>
  );
}
